import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import { usePremiseTypeQuery } from "apis/PremiseType";
import { useApplicantTypeQuery } from "apis/ApplicantType";
import {
  maxLtContractDemandLoad,
  minHtContractDemandLoad,
  premiseNamesForTimeShift,
  regexForName,
} from "common/constant";
import { getConsumerNumberValidation } from "utils/getConsumerNumberValidation";
import { getDocumentsValidationSchema } from "utils/getDocumentsValidationSchema";
import { proofOfOwnershipValidation } from "utils/proofOfOwnershipValidation";

const premisesForHP = ["temporary"];

const useValidationSchema = () => {
  const [premiseIdsForHP, setPremiseIdsForHP] = useState([]);
  const [premiseIdsForTimeShift, setPremiseIdsForTimeShift] = useState([]);

  const formData = useSelector((state) => state.PermanentConnection);

  const { data: { data: premiseType } = {} } = usePremiseTypeQuery({
    utilityId: 1,
  });
  const { data: { data: { items: applicantType = [] } = {} } = {} } =
    useApplicantTypeQuery();

  useEffect(() => {
    if (premiseType) {
      const updatedPremiseIdsForTimeShift = [];
      const updatedPremiseIds = [];
      premiseType.forEach((premise) => {
        if (premiseNamesForTimeShift.includes(premise.name.toLowerCase())) {
          updatedPremiseIdsForTimeShift.push(premise.id);
        }
        if (premisesForHP.includes(premise.name.toLowerCase())) {
          updatedPremiseIds.push(premise.id);
        }
      });

      setPremiseIdsForTimeShift(updatedPremiseIdsForTimeShift);
      setPremiseIdsForHP(updatedPremiseIds);
    }
  }, [premiseType]);

  const validationSchema = useMemo(
    () => [
      Yup.object({
        applicant: Yup.object({
          applicantTypeId: Yup.string().required("Applicant Type is required"),
          applicantName: Yup.string()
            .matches(regexForName, "Please enter a valid name")
            .required("Applicant Name is required"),
          panNumber: Yup.string()
            .matches(
              /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
              "Please enter a valid Pan number"
            ).required("Pan number is required"),
          isGSTRegistered: Yup.string().required("GST is required"),
          gstNumber: Yup.string()
            .test("isGstRequired", "", function (_, context) {
              const [{ value: { isGSTRegistered, gstNumber } } = {}] =
                context.from || [];
              if (isGSTRegistered && !gstNumber) {
                return context.createError({
                  message: "GST number is required",
                });
              }
              return true;
            })
            .matches(
              /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
              "Please enter a valid GST number"
            ),

          occupancyTypeId: Yup.string().required("Occupancy type is required"),
          addressType: Yup.string().required("Address type is required"),
          city: Yup.object({
            id: Yup.number().required("City is required"),
          }),
          correspondenceAddress: Yup.string()
            .matches(/^[\w]+[\w\s.,-]*[\w\s.,-]+/, "Please enter valid address")
            .required("Correspondence Address is required"),
          pinCode: Yup.string()
            .matches(/^\d{6}$/, "Please enter valid pincode")
            .required("Pincode is required"),
          email: Yup.string()
            .email("Please enter valid email")
            .required("Email is required"),
          // .test(
          //   "isEmailVerified",
          //   "Please verify your email with OTP",
          //   (_, context) => {
          //     const [{ value: { isEmailVerified } } = {}] =
          //       context.from || [];
          //     return isEmailVerified;
          //   }
          // ),
          mobileNumber: Yup.string()
            .matches(/^\d{10}$/, "Please enter valid mobile number")
            .required("Mobile number is required"),
          alternateMobileNumber: Yup.string()
            .matches(/^\d{10}$/, "Please enter valid mobile number")
            .test({
              name: "checkIsMobileEqual",
              test: (value) => +value !== formData.applicant.mobileNumber,
              message:
                "Alternate mobile number can't be same as mobile number.",
            }),
          alternateEmail: Yup.array().test(
            "isValidAlternateEmail",
            "",
            function () {
              const [{ value: current = null } = {}] = this.from || [];

              if (
                !!current.alternateEmail.length &&
                !current.alternateEmail.every((email) =>
                  email?.match(
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                  )
                )
              ) {
                return this.createError({
                  message: "Enter valid emails",
                });
              }
              if (
                current.alternateEmail.some((email) => email === current.email)
              ) {
                return this.createError({
                  message: "Alternate email can't be same as email.",
                });
              }
              return true;
            }
          ),

          landLine: Yup.string().matches(
            /^\d{8,11}$/,
            "Please enter a valid landline number"
          ),
          eBillConfirmation: Yup.boolean(),
          isAgriculture: Yup.boolean(),
        }),
      }),
      Yup.object({
        authorizedPerson: Yup.object({
          salutation: Yup.string().required("Salutation is required"),
          firstName: Yup.string()
            .matches(regexForName, "Please enter a valid first name")
            .required("First name is required"),
          middleName: Yup.string().matches(
            regexForName,
            "Please enter a valid middle name"
          ),
          lastName: Yup.string().matches(
            regexForName,
            "Please enter a valid last name"
          ),
          // gender: Yup.string().required("Gender is required"),
          // ageCategory: Yup.string().required("Age category is required"),
          email: Yup.string()
            .email("Please enter valid email")
            .required("Email is required"),
          mobileNumber: Yup.string()
            .required("Mobile number is required")
            .matches(/^\d{10}$/, "Please enter valid mobile number"),
          landLine: Yup.string().matches(
            /^\d{8,11}$/,
            "Please enter a valid landline number"
          ),
          imageUrl: Yup.string().test(
            "imageRequired",
            "Please upload image",
            function () {
              const [{ value: current = null } = {}] = this.from || [];
              return !!(current?.image || current?.imageUrl);
            }
          ),
        }),
      }),
      Yup.object({
        utilityAddress: Yup.object({
          premiseTypeId: Yup.string().required("Premise type is required"),
          premiseUsagePurpose: Yup.string().required(
            "Premise usage purpose is required"
          ),
          premisesAreaId: Yup.string().required("Premise Area is required"),
          zoneName: Yup.string().required("Zone is required"),
          blockName: Yup.string().required("Block is required"),
          buildingName: Yup.string().required("Building is required"),
          roadName: Yup.string().required("Road name is required"),
          // floors: Yup.array().min(1, "Floor is required"),
          otherZone:
            formData.utilityAddress.zoneName === "other"
              ? Yup.string().required("Zone is required")
              : Yup.string(),
          otherBlock:
            formData.utilityAddress.blockName === "other"
              ? Yup.string().required("Block is required")
              : Yup.string(),
          otherBuilding:
            formData.utilityAddress.buildingName === "other"
              ? Yup.string().required("Building is required")
              : Yup.string(),
          otherRoad:
            formData.utilityAddress.roadName === "other"
              ? Yup.string().required("Road is required")
              : Yup.string(),
          unitName: Yup.string().required("Unit ∕ Flat No is required"),
          nearestLandmark: Yup.string(),
        }),
      }),
      Yup.object({
        connectionLoad: Yup.object({
          backupkVA: Yup.number().when("backupRequired", {
            is: true,
            then: (schema) =>
              schema
                .required("Backup voltage is required")
                .test("isValidBackupVoltage", "", function () {
                  const [{ value: current = null } = {}] = this.from || [];
                  if (current.backupkVA <= 0) {
                    return this.createError({
                      message: "Backup voltage must be positive",
                    });
                  }
                  if (current.backupkVA >= 100000) {
                    return this.createError({
                      message:
                        "Maximum value allowed for backup voltage is 99999",
                    });
                  }
                  return true;
                }),
            otherwise: (schema) => schema,
          }),
          loadDetails: Yup.string().required("Load details is required"),
          supplyVoltage: Yup.string().required("Supply voltage is required"),
          tention: Yup.string().required("Please select power requirement"),

          timeShift: premiseIdsForTimeShift.includes(
            formData.utilityAddress.premiseTypeId
          )
            ? Yup.string().required("Time shift is required")
            : Yup.string(),
          isGreenPowerRequired: Yup.boolean(),
          greenPower: formData.connectionLoad?.isGreenPowerRequired
            ? Yup.number()
                .required("Green power is required")
                .integer("Green power should be whole number")
                .test("isValidGreenPower", "", function () {
                  const [{ value: current = null } = {}] = this.from || [];
                  if (current.greenPower <= 0) {
                    return this.createError({
                      message: "Green power must be positive",
                    });
                  }
                  if (current.greenPower > 100) {
                    return this.createError({
                      message: "Maximum green power value is 100",
                    });
                  }
                  return true;
                })
            : Yup.number(),
          averageConsumption: formData.connectionLoad?.isGreenPowerRequired
            ? Yup.number()
                .required("Average consumption is required")
                .integer("Average consumption should be whole number")
                .test("isValidAverageConsumption", "", function () {
                  const [{ value: current = null } = {}] = this.from || [];
                  if (current.averageConsumption <= 0) {
                    return this.createError({
                      message: "Average consumption must be positive",
                    });
                  }
                  if (current.averageConsumption > 100) {
                    return this.createError({
                      message: "Maximum Average consumption value is 100",
                    });
                  }
                  return true;
                })
            : Yup.number(),
          powerRequirement:
            formData.connectionLoad.tention === "LT"
              ? premiseIdsForHP.includes(formData.utilityAddress.premiseTypeId)
                ? Yup.number()
                    .required("Contract load is required")
                    .test("isValidContractLoad", "", function () {
                      const [{ value: current = null } = {}] = this.from || [];
                      if (current.powerRequirement <= 0) {
                        return this.createError({
                          message: "Contract load must be positive",
                        });
                      }
                      if (current.powerRequirement > 125) {
                        return this.createError({
                          message: "contract load should range from 0 to 125",
                        });
                      }
                      return true;
                    })
                : Yup.number()
                    .required("Contract load is required")
                    .test("isValidContractLoad", "", function () {
                      const [{ value: current = null } = {}] = this.from || [];
                      if (current.powerRequirement <= 0) {
                        return this.createError({
                          message: "Contract load must be positive",
                        });
                      }
                      if (current.powerRequirement > maxLtContractDemandLoad) {
                        return this.createError({
                          message: `Maximum value of contract load is ${maxLtContractDemandLoad}`,
                        });
                      }
                      return true;
                    })
              : premiseIdsForHP.includes(formData.utilityAddress.premiseTypeId)
              ? Yup.number()
                  .required("Contract demand is required")
                  .test("isValidContractDemand", "", function () {
                    const [{ value: current = null } = {}] = this.from || [];
                    if (current.powerRequirement <= 0) {
                      return this.createError({
                        message: "Contract demand must be positive",
                      });
                    }
                    if (current.powerRequirement <= 125) {
                      return this.createError({
                        message: "Contract demand should be more than 125",
                      });
                    }
                    if (current.powerRequirement >= 100000) {
                      return this.createError({
                        message:
                          "Maximum value allowed for contract demand is 99999",
                      });
                    }
                    return true;
                  })
              : Yup.number()
                  .required("Contract demand is required")
                  .test("isValidContractDemand", "", function () {
                    const [{ value: current = null } = {}] = this.from || [];
                    if (current.powerRequirement <= 0) {
                      return this.createError({
                        message: "Contract demand must be positive",
                      });
                    }
                    if (current.powerRequirement < minHtContractDemandLoad) {
                      return this.createError({
                        message: "Contract demand should be more than 150",
                      });
                    }
                    if (current.powerRequirement >= 100000) {
                      return this.createError({
                        message:
                          "Maximum value allowed for contract demand is 99999",
                      });
                    }
                    return true;
                  }),
        }),
      }),
      Yup.object({
        documents: getDocumentsValidationSchema(),
        proofOfOwnershipDescription: proofOfOwnershipValidation,
      }),
      Yup.object({
        declaration: Yup.object({
          isConsumerDuesOutStanding: Yup.boolean(),
          consumerDuesOutstanding: Yup.string().when(
            "isConsumerDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isPremisesDuesOutstanding: Yup.boolean(),
          premisesDuesOutstanding: Yup.string().when(
            "isPremisesDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isFirmDuesOutstanding: Yup.boolean(),
          firmDuesOutstanding: Yup.string().when("isFirmDuesOutstanding", {
            is: true,
            then: (schema) => getConsumerNumberValidation(schema),
            otherwise: (schema) => schema,
          }),
        }),
      }),
    ],
    [
      formData.applicant.mobileNumber,
      formData.applicant.email,
      formData.utilityAddress.premiseTypeId,
      formData.utilityAddress.zoneName,
      formData.utilityAddress.blockName,
      formData.utilityAddress.roadName,
      formData.utilityAddress.buildingName,
      formData.connectionLoad.tention,
      formData.connectionLoad.isGreenPowerRequired,
      premiseIdsForHP,
      premiseIdsForTimeShift,
      applicantType,
    ]
  );

  const getValidationSchema = useCallback(
    (index) => validationSchema[index],
    [validationSchema]
  );
  return getValidationSchema;
};

export default useValidationSchema;
